import { useCallback, useRef, useState } from 'react';
import { Alert } from 'rsuite';
import { EngineUpdateCandidateUser, EngineUpdateNotificationOption, EngineUpdateNotificationSettings, EngineUpdateRecipient } from '../types';
import { useEngineUpdateNotificationSettingsQuery, useSaveEngineUpdateNotificationSettingsMutation } from './useEngineUpdateNotificationsQueries';

// Default para uma conta nunca configurada (AC-F3): nenhuma linha salva no backend ainda.
const DEFAULT_SETTINGS: EngineUpdateNotificationSettings = { option: 'NAO_ENVIAR', recipients: [] };

const mergeUniqueRecipients = (current: EngineUpdateRecipient[], added: EngineUpdateRecipient[]) => {
  const currentIds = new Set(current.map(recipient => recipient.cdUsuario));
  const uniqueAdded = added.filter(recipient => !currentIds.has(recipient.cdUsuario));
  return [...current, ...uniqueAdded];
};

export const useEngineUpdateNotificationsController = () => {
  const settingsQuery = useEngineUpdateNotificationSettingsQuery();
  const saveMutation = useSaveEngineUpdateNotificationSettingsMutation();

  const [savedState, setSavedState] = useState<EngineUpdateNotificationSettings>(DEFAULT_SETTINGS);
  const [option, setOption] = useState<EngineUpdateNotificationOption>(DEFAULT_SETTINGS.option);
  const [recipients, setRecipients] = useState<EngineUpdateRecipient[]>(DEFAULT_SETTINGS.recipients);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const hasAppliedLoadedSettings = useRef(false);

  if (!hasAppliedLoadedSettings.current && !settingsQuery.isLoading) {
    const loaded = settingsQuery.data ?? DEFAULT_SETTINGS;
    hasAppliedLoadedSettings.current = true;
    // A primeira renderização após a settings query resolver aplica o estado gravado como
    // rascunho inicial e como base de "cancelar" (AC-F6), sem sobrescrever edições do usuário feitas
    // depois dessa carga.
    setSavedState(loaded);
    setOption(loaded.option);
    setRecipients(loaded.recipients);
  }

  const changeOption = useCallback((nextOption: EngineUpdateNotificationOption) => {
    setOption(nextOption);
  }, []);

  const addRecipients = useCallback((newRecipients: EngineUpdateRecipient[]) => {
    setRecipients(current => mergeUniqueRecipients(current, newRecipients));
  }, []);

  const removeRecipient = useCallback((recipient: EngineUpdateRecipient) => {
    setRecipients(current => current.filter(current_ => current_.cdUsuario !== recipient.cdUsuario));
  }, []);

  const openUserModal = useCallback(() => setIsUserModalOpen(true), []);
  const closeUserModal = useCallback(() => setIsUserModalOpen(false), []);

  const applyUserSelection = useCallback((selectedUsers: EngineUpdateCandidateUser[]) => {
    setRecipients(selectedUsers.map(({ cdUsuario, name, email }) => ({ cdUsuario, name, email })));
    setIsUserModalOpen(false);
  }, []);

  const cancel = useCallback(() => {
    setOption(savedState.option);
    setRecipients(savedState.recipients);
    setIsUserModalOpen(false);
  }, [savedState]);

  const save = useCallback(async () => {
    const payload: EngineUpdateNotificationSettings = { option, recipients };
    try {
      await saveMutation.mutateAsync(payload);
      setSavedState(payload);
      Alert.success('Configuração salva. A regra vale a partir da próxima rodada do motor.');
    } catch {
      Alert.error('Não conseguimos salvar a configuração. Tente novamente em alguns instantes.');
    }
  }, [option, recipients, saveMutation]);

  return {
    data: { option, recipients },
    state: { isUserModalOpen },
    loading: { settings: settingsQuery.isLoading, saving: saveMutation.isLoading },
    handlers: {
      changeOption,
      addRecipients,
      removeRecipient,
      openUserModal,
      closeUserModal,
      applyUserSelection,
      save,
      cancel,
    },
  };
};
